function toTypeLabel(type) {
  if (type === 'success') return 'Diterima';
  if (type === 'danger') return 'Ditolak';
  return 'Pending';
}

function formatDateLabel(date) {
  if (!date) return '-';
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('id-ID', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function mapReviewStatusToType(status) {
  if (status === 'APPROVED') return 'success';
  if (status === 'REJECTED') return 'danger';
  return 'pending';
}

function buildHistoryMessage(review) {
  const notes = review && review.notes_review ? `${review.notes_review}`.trim() : '';

  const rejectionTypes = Array.isArray(review && review.rejection_types)
    ? review.rejection_types.map((item) => item && item.name).filter(Boolean)
    : [];

  let baseMessage = '';
  if (review && review.review_status === 'APPROVED') {
    baseMessage = 'Review Anda telah disetujui admin.';
  } else if (review && review.review_status === 'REJECTED') {
    if (rejectionTypes.length > 0) {
      baseMessage = `Review Anda ditolak: ${rejectionTypes.join(', ')}.`;
    } else {
      baseMessage = 'Review Anda ditolak. Silakan perbaiki data yang diperlukan.';
    }
  } else {
    baseMessage = 'Review Anda sedang diproses oleh admin.';
  }

  if (!notes) return baseMessage;
  return `${baseMessage}\nCatatan Tambahan: ${notes}`;
}

function pickHistoryDate(review) {
  if (!review || typeof review !== 'object') return null;
  return review.reviewed_at || review.reviewedAt || review.created_at || review.createdAt || null;
}

function getTransformedPayload(data) {
  if (data && data.data && data.data.data !== undefined) return data.data.data;
  if (data && data.data !== undefined) return data.data;
  return data;
}

export const state = () => ({
  loading: false,
  uploading: false,
  reviewStatus: 'NOT_REVIEWED',
  certificateFileName: null,
  certificateNumber: null,
  certificateUrl: null,
  certificateFileContentUuid: null,
  checklist: {
    membership: false,
    profile_completion: false,
    photo_profile: false,
    biography: false,
    certificate: false,
  },
  percentageSuccess: 0,
  history: [],
});

export const getters = {};

export const mutations = {
  SET_LOADING(state, payload) {
    state.loading = payload;
  },
  SET_UPLOADING(state, payload) {
    state.uploading = payload;
  },
  SET_REVIEW_STATUS(state, payload) {
    state.reviewStatus = payload;
  },
  SET_CERTIFICATE_FILE_NAME(state, payload) {
    state.certificateFileName = payload;
  },
  SET_CERTIFICATE_NUMBER(state, payload) {
    state.certificateNumber = payload;
  },
  SET_CERTIFICATE_URL(state, payload) {
    state.certificateUrl = payload;
  },
  SET_CERTIFICATE_FILE_CONTENT_UUID(state, payload) {
    state.certificateFileContentUuid = payload;
  },
  SET_CHECKLIST(state, payload) {
    state.checklist = {
      membership: !!payload.membership,
      profile_completion: !!payload.profile_completion,
      photo_profile: !!payload.photo_profile,
      biography: !!payload.biography,
      certificate: !!payload.certificate,
    };
  },
  SET_PERCENTAGE_SUCCESS(state, payload) {
    state.percentageSuccess = Number.isFinite(payload) ? payload : 0;
  },
  SET_HISTORY(state, payload) {
    state.history = payload;
  },
};

export const actions = {
  normalizeReviewStatus(_, status) {
    if (!status) return 'NOT_REVIEWED';
    const normalized = `${status}`.toUpperCase();
    if (normalized === 'PENDING') return 'PENDING';
    if (normalized === 'APPROVED') return 'APPROVED';
    if (normalized === 'REJECTED') return 'REJECTED';
    return 'NOT_REVIEWED';
  },

  async getTimelineProgress({ commit, dispatch, rootState }) {
    if (!rootState.global.loggedIn || !rootState.global.token) {
      return null;
    }

    commit('SET_LOADING', true);
    try {
      const { data } = await this.$http.get('/api/agent_naik_kelas/progress');
      const payload = getTransformedPayload(data) || {};
      const checklistMap = {};

      const checklistItems = Array.isArray(payload.checklist) ? payload.checklist : [];
      checklistItems.forEach((item) => {
        if (item && item.key) {
          checklistMap[item.key] = !!item.is_fulfilled;
        }
      });

      const normalizedReviewStatus = await dispatch(
        'normalizeReviewStatus',
        payload && payload.review ? payload.review.review_status : null,
      );

      const certificateName =
        payload && payload.certificate
          ? payload.certificate.certificate_name ||
            (payload.certificate.file_content_uuid ? 'File sertifikat yang diunggah' : null) ||
            null
          : null;
      const certificateNumber =
        payload && payload.certificate ? payload.certificate.certificate_number || null : null;
      const certificateFileContentUuid =
        payload && payload.certificate ? payload.certificate.file_content_uuid || null : null;

      commit('SET_CHECKLIST', checklistMap);
      commit('SET_REVIEW_STATUS', normalizedReviewStatus);
      commit('SET_CERTIFICATE_FILE_NAME', certificateName);
      commit('SET_CERTIFICATE_NUMBER', certificateNumber);
      commit('SET_CERTIFICATE_FILE_CONTENT_UUID', certificateFileContentUuid);
      commit(
        'SET_CERTIFICATE_URL',
        payload && payload.certificate ? payload.certificate.certificate_url || null : null,
      );
      commit(
        'SET_PERCENTAGE_SUCCESS',
        payload && payload.percentage_success ? Number(payload.percentage_success) : 0,
      );
      return payload;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async getStatusHistory({ commit }) {
    commit('SET_LOADING', true);
    try {
      const { data } = await this.$http.get('/api/agent_naik_kelas/reviews/history');
      const payload = getTransformedPayload(data) || {};
      const reviews = Array.isArray(payload.reviews) ? payload.reviews : [];

      const histories = reviews.map((review) => {
        const reviewStatus =
          review && review.review_status ? `${review.review_status}`.toUpperCase() : '';
        const type = mapReviewStatusToType(reviewStatus);
        const resolvedDate = pickHistoryDate(review);

        return {
          reviewId: review ? review.id : null,
          type,
          typeLabel: toTypeLabel(type),
          message: buildHistoryMessage(review || {}),
          date: resolvedDate,
          dateLabel: formatDateLabel(resolvedDate),
        };
      });

      commit('SET_HISTORY', histories);
      return histories;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async getStatusHistoryDetail(_, reviewId) {
    if (!reviewId) return null;
    const { data } = await this.$http.get(`/api/agent_naik_kelas/reviews/history/${reviewId}`);
    const payload = getTransformedPayload(data) || {};
    return payload.review || null;
  },

  async uploadCertificateFileContent({ commit }, file) {
    commit('SET_UPLOADING', true);
    try {
      const uploadFilePayload = new FormData();
      uploadFilePayload.append('file', file);

      const uploadFileResponse = await this.$http.post(
        '/api/agent_naik_kelas/certificates/upload_file_content',
        uploadFilePayload,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      );
      const fileContentData = getTransformedPayload(uploadFileResponse.data) || {};
      const fileContentUuid = fileContentData.file_content_uuid;
      if (!fileContentUuid) {
        throw new Error('File content uuid not found');
      }

      return fileContentData;
    } finally {
      commit('SET_UPLOADING', false);
    }
  },

  async saveCertificate({ commit }, requestData) {
    commit('SET_UPLOADING', true);
    try {
      const { data } = await this.$http.post('/api/agent_naik_kelas/certificates/upload', {
        file_content_uuid: requestData.file_content_uuid,
      });

      return getTransformedPayload(data) || {};
    } finally {
      commit('SET_UPLOADING', false);
    }
  },
};
